/**
 * Vocabulario dos dois funis num lugar so.
 *
 * As duas paginas (carrinho e expirados) sao a MESMA tela com palavras
 * diferentes: "abandonaram" vira "venceram", "{produto}" vira "{plano}",
 * "Por oferta" vira "Por plano". Concentrar isso aqui e o que permite um
 * unico `PaginaDoFunil` renderizar as duas rotas sem `if (kind === ...)`
 * espalhado pelos componentes -- e garante que uma correcao de texto vale
 * para os dois funis ao mesmo tempo.
 */

import type { BadgeTone, IconName } from '../../components/ui';
import type { CreditChannel, FunnelKind, FunnelLeadStatus } from './types';

export interface Degrau {
  readonly eyebrow: string;
  readonly label: string;
}

export interface Vocabulario {
  readonly titulo: string;
  readonly tituloCurto: string;
  readonly icone: IconName;
  readonly badgeTipo: string;
  readonly badgeTone: BadgeTone;
  /** Complemento da linha "Responsavel: X · ...". */
  readonly descricao: string;
  readonly abaLeads: string;
  readonly abaLeadsIcone: IconName;
  readonly soRecorrentes: boolean;
  readonly entradaKpi: string;
  readonly entradaApoio: string;
  readonly recuperadosKpi: string;
  readonly taxaApoio: string;
  readonly caminhoTitulo: string;
  readonly degraus: readonly [Degrau, Degrau, Degrau];
  readonly reguaTitulo: string;
  readonly reguaSubtitulo: string;
  readonly ladoEntradaTitulo: string;
  readonly ladoEntradaApoio: string;
  readonly ladoSaidaTitulo: string;
  readonly ladoSaidaApoio: string;
  readonly recebeu: string;
  readonly tabelaTitulo: string;
  readonly colunaOferta: string;
  readonly variavelProduto: string;
  readonly colunaEntrou: string;
  readonly colunaVoltou: string;
  readonly notasDoCaminho: readonly string[];
  readonly notasDaRegua: readonly string[];
  /** Nome do instante zero da regua ("abandono", "vencimento"). */
  readonly fato: string;
  /** Rotulo do marco zero na linha do tempo ("Abandonou", "Venceu"). */
  readonly fatoRotulo: string;
  /**
   * Antes disto NAO existe toque: no carrinho a pessoa so "abandonou" depois
   * de 30 min sem pagar; nos expirados so depois do vencimento. Toque antes
   * do fato e lembrete pra quem ainda nao saiu -- e nao ha recuperacao, nem
   * comissao, sobre quem nao saiu.
   */
  readonly atrasoMinimoMinutos: number;
  readonly atrasoMinimoTexto: string;
  /** Uma frase que explica por que a regua so comeca depois do fato. */
  readonly notaDepoisDoFato: string;
}

export const VOCABULARIO: Readonly<Record<FunnelKind, Vocabulario>> = {
  carrinho_abandonado: {
    titulo: 'Funil de Carrinho Abandonado',
    tituloCurto: 'Carrinho abandonado',
    icone: 'carrinho',
    badgeTipo: 'Carrinho abandonado',
    badgeTone: 'accent',
    descricao: 'quem preencheu o checkout e não pagou em 30 min — e quem voltou e pagou depois.',
    abaLeads: 'Carrinhos abandonados',
    abaLeadsIcone: 'leads',
    soRecorrentes: false,
    entradaKpi: 'Abandonaram',
    entradaApoio: 'não pagaram em 30 min',
    recuperadosKpi: 'Recuperados',
    taxaApoio: 'de quem abandonou no período',
    caminhoTitulo: 'O caminho do carrinho',
    degraus: [
      { eyebrow: '30 min sem pagar', label: 'abandonaram' },
      { eyebrow: 'Pagamento', label: 'recuperados' },
      { eyebrow: 'Sem pagar', label: 'ainda pra recuperar' },
    ],
    reguaTitulo: 'A régua de recuperação',
    reguaSubtitulo: 'Cada toque só fala com quem passou pelos anteriores sem pagar.',
    ladoEntradaTitulo: 'Entraram na régua',
    ladoEntradaApoio: 'abandonaram o carrinho',
    ladoSaidaTitulo: 'Não voltou',
    ladoSaidaApoio: 'passaram por todos os toques',
    recebeu: 'receberam a mensagem',
    tabelaTitulo: 'Por oferta',
    colunaOferta: 'Oferta',
    variavelProduto: '{produto}',
    colunaEntrou: 'Abandonou em',
    colunaVoltou: 'Recuperado em',
    notasDoCaminho: [
      'Recuperado só conta pro funil por dois caminhos: a pessoa pagou pelo botão da mensagem (ou durante a ligação), ou respondeu à mensagem e a vendedora fechou a venda por lá — venda com dona, em até 7 dias (ADR-001).',
      'A régua só começa depois de 30 minutos sem pagamento. Antes disso a pessoa ainda está comprando: não há o que recuperar — e a comissão do Converte Fácil existe justamente porque recuperamos quem já tinha saído.',
    ],
    notasDaRegua: [
      '"Clicaram no botão" conta pessoas, não toques: quem abriu o link três vezes é uma só. Clicar não tira ninguém da régua — quem clica e não paga continua na fila e recebe o próximo toque.',
      'Cada toque só fala com quem passou por todos os anteriores sem pagar. Quem paga, responde ou pede pra sair deixa a régua na hora. O verde é quem o funil recuperou naquela janela: pagou pelo botão ou respondeu e a vendedora fechou.',
    ],
    fato: 'abandono',
    fatoRotulo: 'Abandonou',
    atrasoMinimoMinutos: 30,
    atrasoMinimoTexto: '30 minutos',
    notaDepoisDoFato:
      'Nenhum toque sai antes de 30 minutos sem pagamento: até ali a pessoa ainda está no checkout e um contato seria lembrete, não recuperação. Só existe comissão sobre quem já tinha saído.',
  },
  expirados: {
    titulo: 'Funil de Expirados',
    tituloCurto: 'Expirados',
    icone: 'calendario',
    badgeTipo: 'Expirados',
    badgeTone: 'brand',
    descricao: 'quem teve a assinatura vencida — e quem voltou a pagar depois.',
    abaLeads: 'Expirados',
    abaLeadsIcone: 'calendario',
    soRecorrentes: true,
    entradaKpi: 'Venceram',
    entradaApoio: 'assinaturas que chegaram ao fim',
    recuperadosKpi: 'Voltaram',
    taxaApoio: 'do funil, de quem venceu no período',
    caminhoTitulo: 'O caminho de quem venceu',
    degraus: [
      { eyebrow: 'Assinatura', label: 'venceram' },
      { eyebrow: 'Pagamento', label: 'voltaram' },
      { eyebrow: 'Sem voltar', label: 'ainda fora' },
    ],
    reguaTitulo: 'A régua de recuperação',
    reguaSubtitulo: 'Cada marco é um dia exato depois do vencimento. Cada toque só fala com quem passou pelos anteriores sem voltar.',
    ladoEntradaTitulo: 'Venceram',
    ladoEntradaApoio: 'entraram na régua',
    ladoSaidaTitulo: 'Não voltou',
    ladoSaidaApoio: 'passaram por todos os marcos',
    recebeu: 'disparados no período',
    tabelaTitulo: 'Por plano',
    colunaOferta: 'Plano',
    variavelProduto: '{plano}',
    colunaEntrou: 'Venceu em',
    colunaVoltou: 'Voltou em',
    notasDoCaminho: [
      'Venceram = a assinatura chegou ao fim no período e a pessoa ficou de fato de fora (a mesma pessoa conta uma vez, pela assinatura que encerrou por último). Quem renovou em dia nunca aparece aqui: a renovação empurra o vencimento, então a assinatura não termina no período.',
      'Voltaram só conta pro funil por dois caminhos: a pessoa pagou pelo botão da mensagem, ou respondeu e a vendedora fechou a venda por lá — venda com dona, em até 7 dias (ADR-001).',
      'A régua só começa depois do vencimento: quem está em dia nunca recebe toque. Não avisamos ninguém de que o plano vai vencer — a comissão do Converte Fácil existe porque recuperamos quem já tinha saído.',
    ],
    notasDaRegua: [
      '"Na fila hoje" é de hoje, não do período: é quem o disparo pega agora. "Disparados no período" segue a janela do cabeçalho.',
      'Cada marco só fala com quem passou pelos anteriores sem voltar. Quem paga, responde ou pede pra sair deixa a régua na hora; clicar sem pagar não tira ninguém da fila.',
    ],
    fato: 'vencimento',
    fatoRotulo: 'Venceu',
    atrasoMinimoMinutos: 1440,
    atrasoMinimoTexto: '1 dia',
    notaDepoisDoFato:
      'Nenhum marco fica antes de D+1: até o vencimento a pessoa ainda é assinante e um contato seria aviso de renovação, não recuperação. Só existe comissão sobre quem já tinha saído.',
  },
};

// -----------------------------------------------------------------------------
// Variaveis da copy
// -----------------------------------------------------------------------------

export interface VariavelDaCopy {
  readonly nome: string;
  /** `obrigatoria`: sem ela a mensagem nao cumpre o papel na regua. */
  readonly papel: 'obrigatoria' | 'recomendada' | 'opcional';
  readonly porQue: string;
}

/**
 * Variaveis que a mensagem de WhatsApp / e-mail pode usar, na ordem em que o
 * formulario as lista. `{link}` e obrigatoria SO quando nao ha botao de link:
 * uma mensagem sem caminho para pagar nao recupera ninguem (a regra mora em
 * `variaveisDaCopy`, que recebe os botoes).
 */
export function variaveisDaCopy(kind: FunnelKind, canal: 'voice' | 'whatsapp' | 'email', temBotaoDeLink: boolean): readonly VariavelDaCopy[] {
  const produto = VOCABULARIO[kind].variavelProduto;
  const lista: VariavelDaCopy[] = [
    { nome: '{primeiro_nome}', papel: 'obrigatoria', porQue: 'Mensagem sem nome é lida como disparo em massa e cai na resposta.' },
    { nome: produto, papel: 'recomendada', porQue: kind === 'expirados' ? 'Diz qual plano venceu; quem tem mais de um precisa saber de qual estamos falando.' : 'Diz o que ficou no checkout; sem isso a pessoa não lembra do que se trata.' },
  ];
  if (canal === 'voice') {
    lista.push({ nome: '{empresa}', papel: 'recomendada', porQue: 'A IA se apresenta em nome da empresa; sem isso a ligação parece golpe.' });
    return lista;
  }
  lista.push({
    nome: '{link}',
    papel: canal === 'email' || !temBotaoDeLink ? 'obrigatoria' : 'opcional',
    porQue: canal === 'email' ? 'O e-mail precisa do endereço no texto: nem todo leitor de e-mail mostra o botão.' : temBotaoDeLink ? 'Já há botão de link; o {link} no texto é reforço.' : 'Sem botão de link, o {link} é o único caminho para pagar — e pagar pelo link é o que conta como recuperado.',
  });
  return lista;
}

const PADRAO_VARIAVEL = /\{([a-z_]+)\}/g;

/** Nomes de variavel presentes no texto, ja com chaves, sem repetir. */
export function variaveisNoTexto(texto: string): readonly string[] {
  const vistas = new Set<string>();
  for (const m of texto.matchAll(PADRAO_VARIAVEL)) {
    vistas.add(`{${m[1] ?? ''}}`);
  }
  return Array.from(vistas);
}

/**
 * Limites de tamanho que o canal impoe. WhatsApp: corpo de modelo ate 1.024
 * caracteres, botao ate 20; e-mail: assunto ate 78 para nao cortar na caixa
 * de entrada. Sao limites de PLATAFORMA, nao de gosto -- passar deles e a
 * mensagem chegar cortada ou nem sair.
 */
export const LIMITES = {
  whatsappCorpo: 1_024,
  whatsappBotao: 20,
  emailAssunto: 78,
  emailPreheader: 110,
  roteiroVoz: 1_200,
} as const;

/** "T+30min", "T+1h", "T+24h" no carrinho; "D+15", "D+30" nos expirados. */
export function rotuloDoAtraso(kind: FunnelKind, minutes: number): string {
  if (kind === 'expirados') {
    return `D+${Math.max(1, Math.round(minutes / 1440))}`;
  }
  if (minutes < 60) {
    return `T+${minutes}min`;
  }
  if (minutes % 60 === 0) {
    return `T+${minutes / 60}h`;
  }
  return `T+${Math.floor(minutes / 60)}h${String(minutes % 60).padStart(2, '0')}`;
}

/** Legenda do canto direito do cartao do toque: "1º toque" ou "Venceu há 2 semanas". */
export function rotuloDoMarco(kind: FunnelKind, order: number, minutes: number, isLast: boolean): string {
  if (kind === 'carrinho_abandonado') {
    return isLast ? 'Último aviso' : `${order}º toque`;
  }
  if (isLast) {
    return 'Último contato';
  }
  const dias = Math.round(minutes / 1440);
  if (dias % 30 === 0) {
    const meses = dias / 30;
    return meses === 1 ? 'Venceu há 1 mês' : `Venceu há ${meses} meses`;
  }
  if (dias % 7 === 0) {
    const semanas = dias / 7;
    return semanas === 1 ? 'Venceu há 1 semana' : `Venceu há ${semanas} semanas`;
  }
  return `Venceu há ${dias} dias`;
}

/** Como um atraso e editado no formulario: unidade natural para cada funil. */
export type UnidadeDeAtraso = 'min' | 'h' | 'd';

export const MINUTOS_POR_UNIDADE: Readonly<Record<UnidadeDeAtraso, number>> = { min: 1, h: 60, d: 1440 };

export const UNIDADE_LABEL: Readonly<Record<UnidadeDeAtraso, string>> = { min: 'minutos', h: 'horas', d: 'dias' };

export function unidadeNatural(minutes: number): UnidadeDeAtraso {
  if (minutes >= 1440 && minutes % 1440 === 0) {
    return 'd';
  }
  if (minutes >= 60 && minutes % 60 === 0) {
    return 'h';
  }
  return 'min';
}

export interface CanalInfo {
  readonly label: string;
  readonly curto: string;
  readonly icon: IconName;
  readonly tone: BadgeTone;
  /** Slot de serie 1..8 para os graficos, NA ORDEM. */
  readonly serie: 1 | 2 | 3 | 4;
}

export const CANAL: Readonly<Record<CreditChannel, CanalInfo>> = {
  voice: { label: 'Ligação da IA', curto: 'pela ligação', icon: 'telefone', tone: 'accent', serie: 1 },
  whatsapp: { label: 'WhatsApp', curto: 'pelo botão', icon: 'whatsapp', tone: 'info', serie: 2 },
  email: { label: 'E-mail', curto: 'pelo e-mail', icon: 'email', tone: 'brand', serie: 3 },
  seller: { label: 'Vendedora', curto: 'pela vendedora', icon: 'usuarios', tone: 'violet', serie: 4 },
};

export const STATUS_LEAD: Readonly<Record<FunnelLeadStatus, { readonly label: string; readonly tone: BadgeTone }>> = {
  na_regua: { label: 'Na régua', tone: 'neutral' },
  pagou: { label: 'Pagou', tone: 'good' },
  com_comercial: { label: 'Com o comercial', tone: 'violet' },
  vendedora_fechou: { label: 'Vendedora fechou', tone: 'good' },
  descadastrou: { label: 'Pediu pra sair', tone: 'warning' },
  sem_voltar: { label: 'Fim sem voltar', tone: 'critical' },
  voltou_sozinho: { label: 'Voltou sozinho', tone: 'info' },
};
